import axios, { AxiosInstance, AxiosError } from 'axios';

// API設定
const API_CONFIG = {
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000',
  timeout: 30000, // 30秒タイムアウト
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  // CORS対応
  withCredentials: true,
  // リトライ設定
  retry: 3,
  retryDelay: 1000,
};

// APIクライアントインスタンス
const apiClient: AxiosInstance = axios.create(API_CONFIG);

// 認証トークン管理
export const setAuthToken = (token: string | null): void => {
  if (token) {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    sessionStorage.setItem('mederi_admin_auth_token', token);
  } else {
    delete apiClient.defaults.headers.common['Authorization'];
    sessionStorage.removeItem('mederi_admin_auth_token');
  }
};

// 初期化時にトークンを設定
const storedToken = sessionStorage.getItem('mederi_admin_auth_token');
if (storedToken) {
  setAuthToken(storedToken);
}

// リクエストインターセプター
apiClient.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// レスポンスインターセプター
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: AxiosError) => {
    
    // 認証エラーの場合はトークンをクリア
    if (error.response?.status === 401) {
      setAuthToken(null);
      // 必要に応じてログイン画面にリダイレクト
    }
    
    return Promise.reject(error);
  }
);

// API型定義
export interface CsvRegisterRequest {
  csvData: {
    headers: string[];
    rows: string[][];
  };
  validationResults?: {
    totalRows: number;
    validRows: number;
    errorRows: number;
    duplicateRows: number;
  };
  metadata: {
    fileName: string;
    fileSize: number;
    encoding: 'UTF-8' | 'Shift-JIS' | 'auto';
    uploadedAt: string;
    uploadedBy?: string;
  };
  user_id: number;
}

export interface CsvRegisterResponse {
  success: true;
  data: {
    registrationId: string;
    status: string;
    results: {
      successCount: number;
      failureCount: number;
      errors: Array<{
        row: number;
        error: string;
        data: string[];
        timestamp: string;
      }>;
      processedRecords: any[];
    };
    metadata: {
      fileName: string;
      fileSize: number;
    };
    processingTime: number;
  };
}

export interface StatusResponse {
  success: true;
  data: {
    registrationId: string;
    status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
    progress: {
      currentRow: number;
      totalRows: number;
      percentage: number;
    };
    results: {
      successCount: number;
      failureCount: number;
      newRecords: number;
      updatedRecords: number;
    };
    estimatedTimeRemaining: number;
  };
}

export interface ApiErrorResponse {
  success: false;
  error: {
    code: string;
    message: string;
    timestamp: string;
    details?: any;
  };
}

// API関数群
export const medicalListApi = {
  // CSV一括登録
  registerCsv: async (requestData: CsvRegisterRequest): Promise<CsvRegisterResponse> => {
    const response = await apiClient.post<CsvRegisterResponse>('/api/medical-list/csv/register', requestData);
    return response.data;
  },

  // 登録処理状況確認
  getRegistrationStatus: async (registrationId: string): Promise<StatusResponse> => {
    const response = await apiClient.get<StatusResponse>(`/api/medical-list/csv/register/${registrationId}`);
    return response.data;
  },

  // 登録処理キャンセル（必要に応じて実装）
  cancelRegistration: async (registrationId: string): Promise<{ success: boolean }> => {
    const response = await apiClient.delete(`/api/medical-list/csv/register/${registrationId}`);
    return response.data;
  },
};

// 認証トークンの状態確認
export const getAuthTokenStatus = () => {
  const token = sessionStorage.getItem('mederi_admin_auth_token');
  const hasAuthHeader = !!apiClient.defaults.headers.common['Authorization'];
  
  return {
    hasToken: !!token,
    token: token ? '***' : null,
    hasAuthHeader,
    authHeader: hasAuthHeader ? 'Bearer ***' : null
  };
};

// エラーハンドリング用ユーティリティ
export const handleApiError = (error: any): string => {
  const authStatus = getAuthTokenStatus();
  
  // 認証関連エラーの詳細処理
  if (error.response?.status === 401) {
    return `認証エラーが発生しました（401 Unauthorized）\n\n原因:\n- トークンが無効または期限切れ\n- トークンが正しく設定されていない\n\n現在の認証状態:\n- トークン: ${authStatus.hasToken ? `設定済み (${authStatus.token})` : '未設定'}\n- リクエストURL: ${error.config?.baseURL}${error.config?.url}\n\n対処法:\n1. 正しい認証トークンを取得して設定してください\n2. バックエンドのLaravel Sanctum設定を確認してください`;
  }
  
  if (error.response?.status === 403) {
    return `アクセス権限エラーが発生しました（403 Forbidden）\n\n原因:\n- このリソースへのアクセス権限がありません\n\nリクエストURL: ${error.config?.baseURL}${error.config?.url}`;
  }

  // Laravel Sanctum関連エラーの処理
  if (error.response?.data?.message?.includes('Auth driver') || 
      error.response?.data?.message?.includes('sanctum')) {
    return `認証設定エラーが発生しました。\n- 詳細: ${error.response.data.message}\n- バックエンドの認証設定を確認してください\n- URL: ${error.config?.baseURL}${error.config?.url}`;
  }

  // サーバーエラーの詳細処理
  if (error.response?.status === 404) {
    return `リソースが見つかりません（404 Not Found）\n\nリクエストURL: ${error.config?.baseURL}${error.config?.url}\n\n原因:\n- APIエンドポイントが実装されていない\n- URLが間違っている`;
  }
  
  if (error.response?.status === 422) {
    const errorData = error.response.data;
    
    // バックエンドからのエラー形式に対応
    if (errorData?.error?.details) {
      // 詳細なエラー情報がある場合
      const details = Array.isArray(errorData.error.details) 
        ? errorData.error.details.map((d: any) => `行${d.row}: ${d.message}`).join('\n')
        : JSON.stringify(errorData.error.details, null, 2);
      return `バリデーションエラー（422）\n\n${errorData.error.message}\n\n詳細:\n${details}`;
    }
    
    // 従来の形式
    const validationErrors = errorData?.errors || {};
    const errorMessages = Object.entries(validationErrors)
      .map(([field, messages]) => `- ${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
      .join('\n');
    return `バリデーションエラー（422 Unprocessable Entity）\n\n${errorMessages || errorData?.message || '入力データにエラーがあります'}`;
  }
  
  if (error.response?.status === 500) {
    return `サーバーエラーが発生しました（500 Internal Server Error）\n\nバックエンドのログを確認してください。\n\n詳細: ${error.response.data?.message || '不明なエラー'}`;
  }
  
  if (error.response?.data?.error) {
    const apiError = error.response.data.error as ApiErrorResponse['error'];
    
    switch (apiError.code) {
      case 'VALIDATION_ERROR': {
        // バックエンドのFormRequestはerror.detailsに項目別メッセージを返す。
        // どの項目が原因か分かるよう詳細を表示する
        const details = (error.response.data.error as { details?: Record<string, string[] | string> }).details;
        if (details && typeof details === 'object' && Object.keys(details).length > 0) {
          const lines = Object.values(details)
            .map((messages) => `- ${Array.isArray(messages) ? messages.join(' / ') : messages}`)
            .join('\n');
          return `入力データにエラーがあります:\n${lines}`;
        }
        return `入力データにエラーがあります: ${apiError.message}`;
      }
      case 'INVALID_HEADERS':
        return `CSVヘッダーに不足があります: ${apiError.message}`;
      case 'CSV_PROCESSING_ERROR':
        return `CSVデータの処理中にエラーが発生しました: ${apiError.message}`;
      case 'REGISTRATION_NOT_FOUND':
        return '指定された登録IDが見つかりません';
      case 'STATUS_CHECK_ERROR':
        return 'ステータス確認中にエラーが発生しました';
      default:
        return apiError.message || 'APIエラーが発生しました';
    }
  } else if (error.request) {
    return `サーバーとの通信に失敗しました\n\n原因:\n- バックエンドサーバーが起動していない\n- ネットワークの問題\n- CORS設定の問題\n\n詳細情報:\n- リクエストURL: ${error.config?.baseURL}${error.config?.url}\n- メソッド: ${error.config?.method}\n- 認証トークン: ${authStatus.hasToken ? `設定済み (${authStatus.token})` : '未設定'}\n\n対処法:\n1. バックエンドサーバーが起動しているか確認 (./scripts/server-control.sh status)\n2. ブラウザの開発者ツールのネットワークタブでエラー詳細を確認`;
  } else {
    return `予期しないエラーが発生しました\n\nエラーメッセージ: ${error.message}\n\n詳細はブラウザのコンソールを確認してください。`;
  }
};

export default apiClient;
